// Centralized Google Maps JS API loader.
//
// The API key must always come from the environment — never hardcode it here.
// See .env.example: REACT_APP_GOOGLE_MAPS_API_KEY.
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Libraries requested on the maps script. All current consumers use `marker`.
const GOOGLE_MAPS_LIBRARIES = 'marker';

// Single in-flight promise shared across every caller, so the SDK script is only
// ever injected once no matter how many components request it concurrently.
let loadPromise: Promise<void> | null = null;

/**
 * Loads the Google Maps JS API exactly once and resolves when
 * `window.google.maps` is available. Safe to call from multiple components at
 * the same time — they share one script tag and one in-flight promise.
 *
 * Rejects if REACT_APP_GOOGLE_MAPS_API_KEY is not set, so a missing key surfaces
 * as a clear error instead of a broken script request.
 */
export function loadGoogleMaps(): Promise<void> {
  if (window.google?.maps) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    if (!GOOGLE_MAPS_API_KEY) {
      reject(new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY environment variable'));
      return;
    }

    // A script tag may already exist (injected by another loader before this
    // module was centralized, or a prior call). Poll for readiness — the tag
    // could have finished loading before we could attach a load listener.
    const existing = document.querySelector<HTMLScriptElement>('script[src*="maps.googleapis.com"]');
    if (existing) {
      const interval = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
      existing.addEventListener('error', () => {
        clearInterval(interval);
        reject(new Error('Failed to load Google Maps'));
      });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=${GOOGLE_MAPS_LIBRARIES}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });

  // Allow a fresh attempt on the next call if this load failed (e.g. a transient
  // network error or a missing key that gets configured later).
  loadPromise.catch(() => { loadPromise = null; });

  return loadPromise;
}
